// Phase 6 Step 1 — screen-as-data: the FIELD REGISTRY.
//
// Each clause is the declarative encoding of one (or a small joint set of) imperative guard(s) from
// the old `applyScreenerFilters` (src/screener-filters.js). `build(f)` captures the params a clause
// needs from the flat filter object; `pass(row, p)` is the near-VERBATIM INVERSE of the guard (returns
// true when the row is NOT rejected). Fidelity is the whole game here — this is a semantic re-encoding,
// so every bespoke rule (|| vs ??, the MA both-wide shortcut, ADR's asymmetric unknown handling, the
// token-enum maps, `signal:'none'` as a negation) is reproduced exactly. Equivalence is proven against
// a frozen copy of the original guards in tests/screen_equivalence.test.js.
//
// `neutral(p)` (optional) marks a leaf as a guaranteed no-op — used ONLY by the Step-2 URL codec to omit
// dead clauses for compactness. Step 1 emits ALL clauses (see buildScreenTree), so correctness never
// depends on `neutral`. Pure module: no DOM, no browser globals.

const isFinite_ = Number.isFinite;

// Shared row accessors that carry the exact coercions the guards used.
const avgVolOf = (row) => (row.snapshotVolume || row.avgVol50 || 0);   // NOTE: `||` (0 falls through)
const perf3mOf = (row) => (row.perf3m ?? row.pct3m);                   // NOTE: `??` (0 is kept)

// MA-distance band: reproduces `_maChk` EXACTLY, including the both-ends-wide (<=-999 && >=999)
// pass-all shortcut and the finite requirement when either bound is engaged. Must stay JOINT (min+max
// in one clause) — splitting into two independent leaves diverges when one bound sits at its sentinel
// while the other is engaged and a finite value exceeds the sentinel bound.
const maChk = (val, mn, mx) => (mn <= -999 && mx >= 999) ? true : (isFinite_(val) && val >= mn && val <= mx);

// Ordered list — order is stable so a built tree is deterministic.
export const CLAUSES = [
    { id: 'minPrice', build: f => f.minPrice, neutral: p => !(p > 0),
      pass: (row, p) => !(row.price < p) },

    { id: 'minAvgVol', build: f => f.minAvgVol, neutral: p => !(p > 0),
      pass: (row, p) => !(avgVolOf(row) < p) },

    { id: 'minScore', build: f => f.minScore, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.score) || row.score < p)) },

    { id: 'maxRmv', build: f => f.maxRmv, neutral: p => false, // guard runs unconditionally
      pass: (row, p) => !(isFinite_(row.rmv) && row.rmv > p) },

    { id: 'minBqs', build: f => f.minBqs, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.bqsGrade) || row.bqsGrade < p)) },

    { id: 'minDollarVol', build: f => f.minDollarVol, neutral: p => !(p > 0),
      pass: (row, p) => !((row.dollarVolume || 0) < p) },

    { id: 'minRvol', build: f => f.minRvol, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.rvol) || row.rvol < p)) },

    { id: 'minPerf3m', build: f => f.minPerf3m, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(perf3mOf(row)) || perf3mOf(row) < p)) },

    { id: 'minPerf1d', build: f => f.minPerf1d, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(row.pct1d) || row.pct1d < p)) },

    { id: 'minPerf1w', build: f => f.minPerf1w, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(row.pct1w) || row.pct1w < p)) },

    { id: 'minPerf1m', build: f => f.minPerf1m, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(row.pct1m) || row.pct1m < p)) },

    { id: 'minPerf6m', build: f => f.minPerf6m, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(row.pct6m) || row.pct6m < p)) },

    { id: 'minPerf12m', build: f => f.minPerf12m, neutral: p => !(p > -999),
      pass: (row, p) => !(p > -999 && (!isFinite_(row.pct12m) || row.pct12m < p)) },

    { id: 'rsiBand', build: f => ({ min: f.minRsi, max: f.maxRsi }), neutral: p => !(p.min > 0 || p.max < 100),
      pass: (row, p) => !((p.min > 0 || p.max < 100) && (!isFinite_(row.rsi1d) || row.rsi1d < p.min || row.rsi1d > p.max)) },

    { id: 'minMv', build: f => f.minMv, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.mvPassed) || row.mvPassed < p)) },

    { id: 'ma9', build: f => ({ min: f.min9ema, max: f.max9ema }), neutral: p => p.min <= -999 && p.max >= 999,
      pass: (row, p) => maChk(row.dist9, p.min, p.max) },

    { id: 'ma21', build: f => ({ min: f.min21ema, max: f.max21ema }), neutral: p => p.min <= -999 && p.max >= 999,
      pass: (row, p) => maChk(row.dist21, p.min, p.max) },

    { id: 'ma50', build: f => ({ min: f.min50sma, max: f.max50sma }), neutral: p => p.min <= -999 && p.max >= 999,
      pass: (row, p) => maChk(row.dist50, p.min, p.max) },

    { id: 'ma200', build: f => ({ min: f.min200sma, max: f.max200sma }), neutral: p => p.min <= -999 && p.max >= 999,
      pass: (row, p) => maChk(row.dist200, p.min, p.max) },

    { id: 'vcp', build: f => f.vcp, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && (
          (p === 'confirmed' && !row.vcpConfirmed) ||
          (p === 'any-vcp' && !row.vcpIsVcp) ||
          (['forming', 'coiling', 'breakout'].includes(p) && row.vcpStage !== p))) },

    { id: 'rsnh', build: f => f.rsnh, neutral: p => p !== 'rsnh' && p !== 'rsnhbase',
      pass: (row, p) => !(
          (p === 'rsnh' && row.rsNewHigh !== true) ||
          (p === 'rsnhbase' && !(row.rsNewHigh === true && row.extensionStatus === 'notExtended'))) },

    { id: 'leadership', build: f => f.leadership, neutral: p => !['leaders', 'delta', 'building'].includes(p),
      pass: (row, p) => !(
          (p === 'leaders' && !['Delta', 'RS NH', 'Primed'].includes(row.rsPowerTier)) ||
          (p === 'delta' && row.rsPowerTier !== 'Delta') ||
          (p === 'building' && !['Delta', 'RS NH', 'Primed', 'Building'].includes(row.rsPowerTier))) },

    { id: 'sector', build: f => f.sector, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && row.sector !== p) },

    { id: 'group', build: f => f.group, neutral: p => !p,
      pass: (row, p) => !(p && !(row.industryGroup || '').toLowerCase().includes(p)) },

    { id: 'maxGroupRank', build: f => f.maxGroupRank, neutral: p => !(p < 999),
      pass: (row, p) => !(p < 999 && (!isFinite_(row.groupRank) || row.groupRank > p)) },

    { id: 'minTrend', build: f => f.minTrend, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.trendScore) || row.trendScore < p)) },

    { id: 'minDemand', build: f => f.minDemand, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.demandScore) || row.demandScore < p)) },

    { id: 'minTiming', build: f => f.minTiming, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.timingScore) || row.timingScore < p)) },

    { id: 'signal', build: f => f.signal, neutral: p => p === 'any',
      pass: (row, p) => {
          if (p === 'any') return true;
          if (p === 'alpha') return !!row.hasAlpha;
          if (p === 'beta') return !!row.hasBeta;
          if (p === 'delta') return (row.deltaTrigger || isFinite_(row.deltaDays));
          if (p === 'oprime') return row.omegaSignal === 'OMEGA20';
          if (p === 'onova') return row.omegaSignal === 'OMEGA55';
          if (p === 'otitan') return row.omegaSignal === 'OMEGA_EXIT';
          if (p === 'alphaBeta') return (row.hasAlpha || row.hasBeta);
          if (p === 'omega') return ['OMEGA20', 'OMEGA55', 'OMEGA_EXIT'].includes(row.omegaSignal);
          if (p === 'none') return !(row.hasAlpha || row.hasBeta || row.deltaTrigger ||
              isFinite_(row.deltaDays) || ['OMEGA20', 'OMEGA55', 'OMEGA_EXIT'].includes(row.omegaSignal));
          return true;
      } },

    { id: 'tier', build: f => f.tier, neutral: p => p === 'any',
      pass: (row, p) => {
          if (p === 'any') return true;
          if (!isFinite_(row.tier)) return false;
          if (p === '1-2') return !(row.tier > 2);
          if (p === '1-3') return !(row.tier > 3);
          if (!p.includes('-')) return row.tier === Number(p);
          return true;
      } },

    { id: 'stage', build: f => f.stage, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && (!isFinite_(row.stage) || row.stage !== Number(p))) },

    { id: 'channel', build: f => f.channel, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && (!isFinite_(row.channelStage) || row.channelStage !== Number(p))) },

    { id: 'omega', build: f => f.omega, neutral: p => p === 'any',
      pass: (row, p) => !(
          (p === 'buy20' && row.omegaSignal !== 'OMEGA20') ||
          (p === 'buy55' && row.omegaSignal !== 'OMEGA55') ||
          (p === 'exit' && row.omegaSignal !== 'OMEGA_EXIT') ||
          (p === 'none' && ['OMEGA20', 'OMEGA55', 'OMEGA_EXIT'].includes(row.omegaSignal))) },

    { id: 'rmvZone', build: f => f.rmvZone, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && row.rmvZone !== p) },

    { id: 'volumeSignal', build: f => f.volumeSignal, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && (
          (p === 'z' && !['accum', 'dist'].includes(row.volumeSignal?.type)) ||
          (p === 'none' && row.volumeSignal?.type !== 'none') ||
          (!['z', 'none'].includes(p) && row.volumeSignal?.type !== p))) },

    { id: 'extension', build: f => f.extension, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && row.extensionStatus !== p) },

    { id: 'epsAccel', build: f => f.epsAccel, neutral: p => p !== 'yes' && p !== 'no',
      pass: (row, p) => !(
          (p === 'yes' && row.epsAcceleration !== true) ||
          (p === 'no' && row.epsAcceleration === true)) },

    { id: 'salesAccel', build: f => f.salesAccel, neutral: p => p !== 'yes' && p !== 'no',
      pass: (row, p) => !(
          (p === 'yes' && row.salesAcceleration !== true) ||
          (p === 'no' && row.salesAcceleration === true)) },

    { id: 'growth', build: f => f.growth, neutral: p => p === 'any',
      pass: (row, p) => !(p !== 'any' && row.growthStatus !== p) },

    // ADR band — asymmetric unknown handling: minAdr EXCLUDES unknown ADR; maxAdr KEEPS unknown ADR.
    { id: 'minAdr', build: f => f.minAdr, neutral: p => !(p > 0),
      pass: (row, p) => !(p > 0 && (!isFinite_(row.adrPct) || row.adrPct < p)) },

    { id: 'maxAdr', build: f => f.maxAdr, neutral: p => !(p < 100),
      pass: (row, p) => !(p < 100 && isFinite_(row.adrPct) && row.adrPct > p) },
];

export const CLAUSE_BY_ID = CLAUSES.reduce((m, c) => { m[c.id] = c; return m; }, {});
